import { Loader2 } from "lucide-react";
import { useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate } from "react-router";
import { Brand } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { supabase } from "@/lib/supabase";

/**
 * Anmeldung mit Passwort oder 6-stelligem E-Mail-Code. Bewusst Code statt Magic Link: Auf iOS
 * öffnet ein Link aus der Mail den Browser, nicht die installierte PWA — der Code funktioniert überall.
 */
export function LoginPage() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const [mode, setMode] = useState<"password" | "code">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (session) return <Navigate to="/" replace />;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (mode === "password") {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) setError(t("auth.invalid"));
      } else if (!codeSent) {
        const { error } = await supabase.auth.signInWithOtp({ email, options: { shouldCreateUser: false } });
        if (error) setError(t("errors.generic"));
        else setCodeSent(true);
      } else {
        const { error } = await supabase.auth.verifyOtp({ email, token: code.trim(), type: "email" });
        if (error) setError(t("auth.invalid_code"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1fr_1.1fr]">
      <div className="flex flex-col justify-between px-6 py-8 sm:px-12">
        <Brand />
        <form onSubmit={submit} className="mx-auto w-full max-w-sm space-y-6 py-12">
          <div className="space-y-1.5">
            <h1 className="text-2xl font-semibold">{t("auth.title")}</h1>
            <p className="text-sm text-muted-foreground">{t("auth.subtitle")}</p>
          </div>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input id="email" type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} disabled={codeSent} />
            </div>
            {mode === "password" && (
              <div className="space-y-1.5">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Input id="password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
              </div>
            )}
            {mode === "code" && codeSent && (
              <div className="space-y-1.5">
                <Label htmlFor="code">{t("auth.code")}</Label>
                <Input
                  id="code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  className="tabular text-lg tracking-[0.4em]"
                />
                <p className="text-xs text-muted-foreground">{t("auth.code_sent")}</p>
              </div>
            )}
          </div>
          {error && <p className="rounded-lg bg-destructive-soft px-3 py-2 text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full" disabled={busy}>
            {busy && <Loader2 className="animate-spin" />}
            {mode === "password" ? t("auth.sign_in") : codeSent ? t("auth.verify") : t("auth.send_code")}
          </Button>
          <button
            type="button"
            className="w-full text-center text-sm text-muted-foreground underline-offset-4 hover:underline"
            onClick={() => {
              setMode(mode === "password" ? "code" : "password");
              setCodeSent(false);
              setError(null);
            }}
          >
            {mode === "password" ? t("auth.use_code") : t("auth.use_password")}
          </button>
        </form>
        <p className="text-xs text-muted-foreground">optitime · KFS</p>
      </div>
      <div className="relative hidden overflow-hidden bg-[#1a0a0e] lg:block">
        <LoginArt />
      </div>
    </div>
  );
}

/** Ruhige Markenfläche: Wochenraster mit Arbeitsblöcken in Rot. */
function LoginArt() {
  const rows = [
    [0.08, 0.36, 0.42, 0.74],
    [0.1, 0.38, 0.44, 0.76],
    [0.06, 0.34, 0.41, 0.7],
    [0.12, 0.4, 0.46, 0.8],
    [0.09, 0.37, 0.43, 0.62],
  ];
  return (
    <svg className="absolute inset-0 h-full w-full" viewBox="0 0 600 700" preserveAspectRatio="xMinYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id="fade" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1a0a0e" stopOpacity="0" />
          <stop offset="1" stopColor="#1a0a0e" stopOpacity="0.9" />
        </linearGradient>
      </defs>
      {Array.from({ length: 13 }, (_, i) => (
        <line key={i} x1={60 + i * 40} y1="120" x2={60 + i * 40} y2="560" stroke="#ffffff" strokeOpacity="0.06" />
      ))}
      {rows.map(([a, b, c, d], i) => (
        <g key={i} transform={`translate(60 ${160 + i * 80})`}>
          <rect width="480" height="36" rx="10" fill="#ffffff" fillOpacity="0.03" />
          <rect x={a! * 480} width={(b! - a!) * 480} height="36" rx="10" fill="#c21f3a" />
          <rect x={c! * 480} width={(d! - c!) * 480} height="36" rx="10" fill="#c21f3a" fillOpacity={i === 4 ? 0.45 : 1} />
        </g>
      ))}
      <rect width="600" height="700" fill="url(#fade)" />
      <text x="60" y="640" fill="#ffffff" fillOpacity="0.85" fontFamily="DM Sans Variable, sans-serif" fontSize="22" fontWeight="550">
        Minutengenau. Ohne Rundung.
      </text>
    </svg>
  );
}
